/**
 * Hook for managing report selection state
 * @module wellRegistrationTotals/hooks/useReportSelection
 */

import { useState, useCallback } from "react";
import { CURRENT_YEAR } from "../constants";

/**
 * Manages year selection, row selection, and map view state
 * @returns {Object} Selection state and handlers
 */
export const useReportSelection = () => {
  const [selectedYear, setSelectedYear] = useState(CURRENT_YEAR);
  const [selectedRow, setSelectedRow] = useState(null);
  const [mapView, setMapView] = useState(null);

  /**
   * Handle year change - resets row selection and map view
   */
  const handleYearChange = useCallback((e) => {
    setSelectedYear(e.target.value);
    setSelectedRow(null);
    setMapView(null);
  }, []);

  /**
   * Handle row click - toggles selection
   * If clicking the same row, deselects it
   */
  const handleRowClick = useCallback((row) => {
    setSelectedRow((prev) => {
      const isDeselecting =
        prev?.period_start === row.period_start &&
        prev?.period_end === row.period_end;

      if (isDeselecting) {
        setMapView(null);
        return null;
      }

      setMapView(null);
      return row;
    });
  }, []);

  /**
   * Close drilldown panel and reset selection
   */
  const handleCloseDrilldown = useCallback(() => {
    setSelectedRow(null);
    setMapView(null);
  }, []);

  /**
   * Update map view state (called when map moves)
   */
  const handleMapViewChange = useCallback((view) => {
    setMapView(view);
  }, []);

  return {
    selectedYear,
    selectedRow,
    mapView,
    handleYearChange,
    handleRowClick,
    handleCloseDrilldown,
    handleMapViewChange,
  };
};
